


























































































import { Component, Vue } from 'vue-property-decorator'
import ApiClient from '@/services/api-client'
import { CvType, CvValue } from '@/models'
import { CvValueUtils } from '@/utils/cv-value-utils'
import UpsertCvTypeModal from '@/components/modals/UpsertCvTypeModal.vue'
import UpsertCvValueModal from '@/components/modals/UpsertCvValueModal.vue'

@Component({
  components: { UpsertCvTypeModal, UpsertCvValueModal }
})
export default class ControlledVocabulary extends Vue {
  private cvTypes: CvType[] = []
  private values: CvValue[] = []
  private selectedTypeId = ''
  private selectedType: CvType | null = null
  private selectedValue: CvValue | null = null
  private loading = false

  private valueFields = [
    { key: 'codeId', label: this.$t('cv_view.columns.code_id'), sortable: true },
    { key: 'name', label: this.$t('cv_view.columns.name'), sortable: true },
    { key: 'code', label: this.$t('cv_view.columns.code'), sortable: true },
    { key: 'shortCode', label: this.$t('cv_view.columns.short_code'), sortable: true },
    { key: 'orderNum', label: this.$t('cv_view.columns.order'), sortable: true, class: 'text-center' },
    { key: 'icon', label: this.$t('cv_view.columns.icon'), sortable: false, class: 'text-center' },
    { key: 'actions', label: this.$t('table.actions'), sortable: false, class: 'text-center' }
  ]

  get selectedTypeName(): string {
    return this.cvTypes.find(t => t.typeId === this.selectedTypeId)?.name || this.selectedTypeId
  }

  async created() {
    await this.loadTypes()
  }

  private async loadTypes() {
    try {
      this.cvTypes = await ApiClient.getCvTypes(true)
    } catch (error) {
      console.error('Error loading CV types:', error)
      this.showError(this.$t('feedback.error_message_generic').toString())
    }
  }

  async loadValues() {
    if (!this.selectedTypeId) {
      this.values = []
      return
    }
    try {
      this.loading = true
      this.values = await ApiClient.getCvValuesByType(this.selectedTypeId, true)
    } catch (error) {
      console.error('Error loading CV values:', error)
      this.showError(this.$t('feedback.error_message_generic').toString())
    } finally {
      this.loading = false
    }
  }

  // ---- CV types ----
  openCreateType() {
    this.selectedType = null
    this.$nextTick(() => this.$bvModal.show('UpsertCvTypeModal'))
  }

  openEditType() {
    if (!this.selectedTypeId) return
    const type = this.cvTypes.find(t => t.typeId === this.selectedTypeId)
    this.selectedType = type ? { ...type } : null
    this.$nextTick(() => this.$bvModal.show('UpsertCvTypeModal'))
  }

  async onTypeSaved(saved: CvType) {
    await this.loadTypes()
    this.selectedTypeId = saved.typeId
    await this.loadValues()
    this.showSuccess(this.$t('cv_view.type_saved_success').toString())
  }

  async confirmDeleteType() {
    if (!this.selectedTypeId) return
    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('cv_view.confirm_delete_type', { type: this.selectedTypeName }).toString(),
      {
        title: this.$t('cv_view.confirm_delete_type_title').toString(),
        okVariant: 'danger',
        okTitle: this.$t('button_actions.remove').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )
    if (!confirmed) return
    try {
      await ApiClient.deleteCvType(this.selectedTypeId)
      CvValueUtils.invalidate(this.selectedTypeId)
      this.selectedTypeId = ''
      this.values = []
      await this.loadTypes()
      this.showSuccess(this.$t('cv_view.type_deleted_success').toString())
    } catch (error: any) {
      console.error('Error deleting CV type:', error)
      this.showError(error.message || this.$t('feedback.error_message_generic').toString())
    }
  }

  // ---- CV values ----
  openCreateValue() {
    this.selectedValue = null
    this.$nextTick(() => this.$bvModal.show('UpsertCvValueModal'))
  }

  openEditValue(value: CvValue) {
    this.selectedValue = { ...value }
    this.$nextTick(() => this.$bvModal.show('UpsertCvValueModal'))
  }

  async onValueSaved() {
    CvValueUtils.invalidate(this.selectedTypeId)
    await this.loadValues()
    this.showSuccess(this.$t('cv_view.value_saved_success').toString())
  }

  async confirmDeleteValue(value: CvValue) {
    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('cv_view.confirm_delete_value', { value: value.name || value.codeId }).toString(),
      {
        title: this.$t('cv_view.confirm_delete_value_title').toString(),
        okVariant: 'danger',
        okTitle: this.$t('button_actions.remove').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )
    if (!confirmed) return
    try {
      await ApiClient.deleteCvValue(value.codeId)
      CvValueUtils.invalidate(this.selectedTypeId)
      await this.loadValues()
      this.showSuccess(this.$t('cv_view.value_deleted_success').toString())
    } catch (error: any) {
      console.error('Error deleting CV value:', error)
      this.showError(error.message || this.$t('feedback.error_message_generic').toString())
    }
  }

  // ---- feedback ----
  private showSuccess(message: string) {
    this.$bvToast.toast(message, {
      title: this.$t('feedback.feedback_message_success_title').toString(),
      variant: 'success',
      solid: true,
      autoHideDelay: 2000
    })
  }

  private showError(message: string) {
    this.$bvToast.toast(message, {
      title: this.$t('feedback.error_title_generic').toString(),
      variant: 'danger',
      solid: true
    })
  }
}
